import { WarehouseItemWorkRequestRejectionReason } from "@redotech/crdb-enums/fulfillment/warehouse-item-work-request-rejection-reason";
import { inventoryItemIdFormat } from "@redotech/redo-inventory-id";
import {
  photoInstructionStepIdFormat,
  warehouseItemPhotoCaptureIdFormat,
  warehouseItemWorkRequestIdFormat,
} from "@redotech/redo-warehouse-id";
import * as z from "zod";
import {
  ItemPhotoCaptureSchema,
  ItemPhotoRequestDetailSchema,
} from "./item-work-request-types";

/**
 * What a scanned inventory item turned out to be. `rejected` is a request the
 * server just closed as impossible to perform, so the operator is told why.
 */
export const ItemPhotoRequestScanResultSchema = z.discriminatedUnion(
  "outcome",
  [
    z.object({ outcome: z.literal("none") }),
    z.object({
      outcome: z.literal("ready"),
      request: ItemPhotoRequestDetailSchema,
    }),
    z.object({
      outcome: z.literal("rejected"),
      reason: z.enum(WarehouseItemWorkRequestRejectionReason),
    }),
  ],
);

export type ItemPhotoRequestScanResult = z.infer<
  typeof ItemPhotoRequestScanResultSchema
>;

/** Scan lookup: an inventory item id typed or scanned by the operator. */
export const getItemPhotoRequestForItem = {
  name: "getItemPhotoRequestForItem" as const,
  input: z.object({ inventoryItemId: inventoryItemIdFormat.schema }),
  output: z.object({ result: ItemPhotoRequestScanResultSchema }),
};

export const ITEM_PHOTO_UPLOAD_CONTENT_TYPES: readonly string[] = [
  "image/jpeg",
  "image/jpg",
  "image/png",
];

export const ITEM_PHOTO_UPLOAD_MAX_BYTES = 20 * 1024 * 1024;

/**
 * Multipart upload of one captured frame, as `uploadParcelPhoto` does. The
 * first photo also claims the request, so scanning alone does not take it.
 */
export const uploadItemPhotoCapture = {
  name: "uploadItemPhotoCapture" as const,
  input: z.object({
    requestId: warehouseItemWorkRequestIdFormat.schema,
    /** The direction this photo answers; null for an extra, unprompted shot. */
    photoInstructionStepId: photoInstructionStepIdFormat.schema.nullish(),
  }),
  output: z.object({ photo: ItemPhotoCaptureSchema }),
};

/** Discards a shot the operator wants to retake. */
export const deleteItemPhotoCapture = {
  name: "deleteItemPhotoCapture" as const,
  input: z.object({
    requestId: warehouseItemWorkRequestIdFormat.schema,
    photoId: warehouseItemPhotoCaptureIdFormat.schema,
  }),
  output: z.null(),
};

export const ItemWorkRequestPhotoSchema = ItemPhotoCaptureSchema.extend({
  /** The direction the photo answers; null for an unprompted shot. */
  guidance: z.string().nullable(),
});

export type ItemWorkRequestPhoto = z.infer<typeof ItemWorkRequestPhotoSchema>;

/** Photos on a request, for the merchant or warehouse to check them. */
export const getItemWorkRequestPhotos = {
  name: "getItemWorkRequestPhotos" as const,
  input: z.object({ id: warehouseItemWorkRequestIdFormat.schema }),
  output: z.object({ photos: z.array(ItemWorkRequestPhotoSchema) }),
};

export const completeItemWorkRequest = {
  name: "completeItemWorkRequest" as const,
  input: z.object({ requestId: warehouseItemWorkRequestIdFormat.schema }),
  output: z.object({ request: ItemPhotoRequestDetailSchema }),
};
